import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { addBookletMutation, categoriesInBookletQuery, getBookletsInZoneQuery, updateBookletMutation } from '../../queries/queries';
import { Form, Field, Group } from '../../common/index';
import Select from 'react-select';
import FieldWrapper from '../../common/FieldWrapper';
import PaperBookletsCategories from './PaperBookletsCategories';
import { useQuery } from '@apollo/client';
import { IoInformationCircle } from 'react-icons/io5';
import Tooltip from '../../common/Tooltip';

function BookletsForm(props) {
  const { content, onComplete, associations, zoneAssociations, zoneId, refetchQuery } = props;
  const { data: bookletCategories } = useQuery(categoriesInBookletQuery, { variables: { bookletId: content?._id } });

  const [sContent, setSContent] = useState(content);

  useEffect(() => {
    setSContent({
      ...content,
      zoneId: zoneId,
      // Default associations where zoneId matches (if new booklet)
      purchasableThroughAssociations: content?._id
        ? content.purchasableThroughAssociations
        : associations?.filter((a) => a.zoneId === zoneId).map((a) => a._id),
      categorySettings: content?.categorySettings?.map(({ __typename, ...rest }) => rest) || [],
    });
  }, [associations, content, zoneId]);

  const addMonths = (dateStr, months) => {
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + months);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const ss = (b) => {
    const updated = { ...sContent, ...b };
    const months = sContent?.defaultValidityMonths;
    if (months) {
      if (b.buyableFrom) {
        updated.buyableTo = addMonths(b.buyableFrom, months);
      }
      if (b.validFrom) {
        updated.validTo = addMonths(b.validFrom, months);
      }
    }
    setSContent(updated);
  };

  const formatDate = (date) => {
    const parsedDate = new Date(date);
    return isNaN(parsedDate) ? '' : parsedDate.toISOString().slice(0, 10);
  }

  return (
        <Form sContent={sContent} parent="zoneId" get={refetchQuery || getBookletsInZoneQuery} add={addBookletMutation} update={updateBookletMutation} onComplete={onComplete}>
          <Group title="Allmänt">
            <Field type="text" req name="title" title="Titel på häfte *" value={sContent && sContent.title} onChange={ss} />
            <Field type="text" name="subtitle" title="Undertitel" value={sContent && sContent.subtitle} onChange={ss} />
            <Field type="number" req name="price" title="Pris (kr, inkl. moms) *" value={sContent && sContent.price} onChange={ss} />
            <Field type="admins" x2 name="adminEmails" title="Administratörer" value={sContent && sContent.adminEmails} onChange={ss} />
          </Group>
          <Group title={
            <>
              Huvudförening
              <Tooltip content="Huvudföreningen fästs längst upp i listan av föreningar inom zonen, har möjlighet att få stödandel (se nedan) och får tillgång till unikt material i Mina Sidor.">
                <IoInformationCircle size={16} />
              </Tooltip>
            </>
          }>
            <FieldWrapper x2 name="mainAssociation" title="Huvudförening">
              <Select
                options={zoneAssociations && zoneAssociations
                  .map((a) => ({
                    value: a._id,
                    label: a._id === sContent?.mainAssociationId ? `${a.name} (Vald)` : a.name,
                    isSelected: a._id === sContent?.mainAssociationId
                  }))
                  .sort((a, b) => {
                    // Sort selected main association to the top
                    if (a.isSelected && !b.isSelected) return -1;
                    if (!a.isSelected && b.isSelected) return 1;
                    return a.label.localeCompare(b.label);
                  })
                }
                value={sContent && sContent.mainAssociationId && (() => {
                  const association = zoneAssociations.find((a) => a._id === sContent.mainAssociationId);
                  return {
                    value: sContent.mainAssociationId,
                    label: association ? association.name : ''
                  };
                })()}
                placeholder="Välj huvudförening..."
                onChange={(selected) => ss({ mainAssociationId: selected?.value })}
                isClearable
              />
            </FieldWrapper>
          </Group>
          <Group title="Försäljning">
            <Field req type="date" name="buyableFrom" title="Startdatum *" value={sContent && formatDate(sContent.buyableFrom)} onChange={ss} />
            <Field req type="date" name="buyableTo" title="Slutdatum *" value={sContent && formatDate(sContent.buyableTo)} onChange={ss} />
            <FieldWrapper name="associations" title="Säljbart av föreningar" x2>
              <Select
                isMulti
                options={associations && associations.map((a) => ({ value: a._id, label: a.name }))}
                value={sContent && sContent.purchasableThroughAssociations && sContent.purchasableThroughAssociations.map((id) => {
                  const association = associations.find((a) => a._id === id);
                  return {
                    value: id,
                    label: association ? association.name : ''
                  };
                })}
                placeholder="Välj föreningar..."
                onChange={(selected) => ss({ purchasableThroughAssociations: selected?.map((s) => s.value) })}
              />
            </FieldWrapper>
          </Group>
          <Group title="Giltighet">
            <Field req type="date" name="validFrom" title="Startdatum *" value={sContent && formatDate(sContent.validFrom)} onChange={ss} />
            <Field req type="date" name="validTo" title="Slutdatum *" value={sContent && formatDate(sContent.validTo)} onChange={ss} />
          </Group>
          <Group title={
            <Fragment>
              Total andel
              <Tooltip content="Summan som alltid delas ut för varje sålt häfte.">
                <IoInformationCircle size={16} />
              </Tooltip>
            </Fragment>
          }>
            <Field x2 type="number" name="totalCommission" title="Total andel (kr, inkl. moms)" value={sContent && sContent.totalCommission} onChange={ss} />
          </Group>
          <Group title="Stödandel">
            <Field
              type="number"
              name="supportCommissionToMainAssociation"
              title={
                <>
                  Stödandel till huvudförening (kr, inkl. moms)
                  <Tooltip content="Andel av total andel som går till huvudföreningen när köpet sker via en förening.">
                    <IoInformationCircle size={16} />
                  </Tooltip>
                </>
              }
              value={sContent && sContent.supportCommissionToMainAssociation}
              onChange={ss}
            />
            <Field
              type="number"
              name="supportCommissionToAssociation"
              title={
                <>
                  Stödandel till förening (kr, inkl. moms)
                  <Tooltip content="Andel av total andel som eventuellt går till en förening när köpet sker via huvudförening.">
                    <IoInformationCircle size={16} />
                  </Tooltip>
                </>
              }
              value={sContent && sContent.supportCommissionToAssociation}
              onChange={ss}
            />
          </Group>
          
          <Group title="Utseende">
            <Field type="color" name="colorLeft" title="Färg på vänster sida" value={sContent && sContent.colorLeft} onChange={ss} />
            <Field type="color" name="colorRight" title="Färg på höger sida" value={sContent && sContent.colorRight} onChange={ss} />
            <Field type="color" name="colorFont" title="Färg på text" value={sContent && sContent.colorFont} onChange={ss} />
            <Field type="color" name="colorSalesMaterial" title="Färg på försäljningsmaterial" value={sContent && sContent.colorSalesMaterial} onChange={ss} />
            <Field type="image" name="logoURL" title="Logotyp (max 1MB)" value={sContent && sContent.logoURL} onChange={ss} />
          </Group>
          <Group title="Pappershäfte">
            <Field type="textarea" name="textInsidePrintedBooklet" title="Text på insida" value={sContent && sContent.textInsidePrintedBooklet} onChange={ss} rows={9} />
            <Field type="image" name="imageOfBookletInAppURL" title="Bild på häfte i appen (max 1MB)" value={sContent && sContent.imageOfBookletInAppURL} onChange={ss} />
            <Field type="image" name="imageOfSingleBookletInAppURL" title="Bild på enskilt häfte i appen (max 1MB)" value={sContent && sContent.imageOfSingleBookletInAppURL} onChange={ss} />
            <PaperBookletsCategories
              categories={bookletCategories?.categoriesInBooklet}
              categorySettings={sContent?.categorySettings}
              setCategorySettings={(newCategorySettings) => ss({
                categorySettings: newCategorySettings
              })}
            />
          </Group>
        </Form>
  )
}

BookletsForm.propTypes = {
  content: PropTypes.object,
  onComplete: PropTypes.func.isRequired,
  associations: PropTypes.array.isRequired,
  zoneAssociations: PropTypes.array.isRequired,
}

BookletsForm.defaultProps = {
  content: null,
}

export default BookletsForm;
